import { DR_HOFFMANN_SERVICES } from "constants/massages";
import { getNumberLocale } from "./i18n";
import { hasBalancePayment } from "utils/bookingPayment";

const SERVICE_LABEL_TO_KEY = Object.fromEntries(
  DR_HOFFMANN_SERVICES.map((s) => [s.label, s.key])
);

const SERVICE_KEY_TO_LABEL = Object.fromEntries(
  DR_HOFFMANN_SERVICES.map((s) => [s.key, s.label])
);

const LEGACY_SERVICE_LABEL_TO_KEY = {
  "Premium Massage im Studio": "dr-hoffmann-premium-studio",
  "Premium Massage vor Ort": "dr-hoffmann-premium-onsite",
  "Intense+": "dr-hoffmann-intense-plus-business",
  "Classic+ im Studio": "dr-hoffmann-classic-plus-business-studio",
  "Classic+ vor Ort": "dr-hoffmann-classic-plus-business-onsite",
  "Intense+ im Studio": "dr-hoffmann-intense-plus-business-studio",
  "Intense+ vor Ort": "dr-hoffmann-intense-plus-business",
};

const normalizeLegacyServiceLabel = (label) =>
  String(label || "").replace(/^Premium Massage\b/, "Intense Massage");

export const formatLong = (date, dateLocale = "de-DE") =>
  new Date(date).toLocaleString(dateLocale, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const formatShortDate = (date, dateLocale = "de-DE") =>
  new Date(date).toLocaleDateString(dateLocale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

export const formatShortDateTime = (date, dateLocale = "de-DE") =>
  new Date(date).toLocaleString(dateLocale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const formatEuro = (cents, locale = "de") =>
  new Intl.NumberFormat(getNumberLocale(locale), {
    style: "currency",
    currency: "EUR",
  }).format((cents || 0) / 100);

export const MANUAL_REVENUE_CALENDAR_TYPE = "manual-revenue";

export const isManualRevenueCalendarItem = (item) =>
  item?.type === MANUAL_REVENUE_CALENDAR_TYPE;

export const formatRevenueEntryDate = (ymd, dateLocale = "de-DE") => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd || "—";
  const [y, m, d] = ymd.split("-").map(Number);
  return formatShortDate(new Date(y, m - 1, d), dateLocale);
};

export const statusLabel = (t, status) => {
  const map = {
    "pending-email-verification": "status.pendingEmailVerification",
    "pending-employee-confirmation": "status.pendingEmployee",
    "pending-payment": "status.pendingPayment",
    "reserved-awaiting-payment": "status.reservedAwaitingPayment",
    "awaiting-immediate-payment": "status.awaitingImmediatePayment",
    confirmed: "status.confirmed",
    cancelled: "status.cancelled",
    expired: "status.expired",
    completed: "status.completed",
    "no-show": "status.noShow",
  };
  return t(map[status] || status);
};

/** Open Stripe / manual payment holds — not yet paid. */
export const isAwaitingPaymentBooking = (booking) => {
  const status = booking?.status;
  return (
    status === "pending-payment" ||
    status === "reserved-awaiting-payment" ||
    status === "awaiting-immediate-payment"
  );
};

/** Who cancelled a booking (team dashboard). */
export const cancelledByLabel = (t, cancelledBy, staffCancellationReason) => {
  const map = {
    customer: "cancelledBy.customer",
    admin: "cancelledBy.admin",
    staff: "cancelledBy.staff",
  };
  if (cancelledBy && map[cancelledBy]) return t(map[cancelledBy]);
  if (staffCancellationReason?.trim()) return t("cancelledBy.teamLegacy");
  return "";
};

export const paymentLabel = (booking) => {
  if (booking.status === "pending-email-verification") {
    return "Noch nicht verbindlich · E-Mail offen";
  }
  if (
    booking.status === "pending-payment" ||
    booking.status === "reserved-awaiting-payment" ||
    booking.status === "awaiting-immediate-payment"
  ) {
    return "Zahlung ausstehend";
  }
  if (booking.status === "expired") return "Abgelaufen";
  if (hasBalancePayment(booking) && Number(booking.priceCents || 0) === 0) {
    return "Mit Guthaben bezahlt";
  }
  const paid =
    booking.paymentStatus === "paid" || booking.paymentStatus === "not-applicable";
  const paidText = paid ? "Bezahlt" : "Unbezahlt";
  let channel = "Vor Ort";
  if (booking.paymentMethod === "stripe") channel = "Online";
  else if (booking.paymentMethod === "later") channel = "Nach Termin";
  return `${paidText} (${channel})`;
};

export const registrationSiteLabel = (t, site) => {
  if (site === "tawan-spa") return t("site.tawanSpa");
  if (site === "dr-hoffmann-massage") return t("site.drHoffmann");
  return "";
};

/** QR ?source= slug → readable label (any campaign, not only fixed keys). */
export const formatCampaignSource = (source) => {
  const s = String(source || "").trim();
  if (!s) return s;
  return s
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const campaignSourceLabel = (t, source) => {
  if (source === "webseite" || source === "website") return t("source.website");
  if (source === "dr-hoffmann-qr") return t("source.hoffmannQr");
  if (source === "tawan-qr" || source === "tavon-qr") return t("source.tawanQr");
  if (source === "dr-hoffmann") return t("source.drHoffmann");
  return formatCampaignSource(source);
};

/** Registration / booking source; optional registrationSite adds “(Tawan Spa)” etc. */
export const sourceLabel = (t, source, registrationSite) => {
  const base = campaignSourceLabel(t, source);
  const site = registrationSiteLabel(t, registrationSite);
  return site ? `${base} (${site})` : base;
};

/** Overview “Kunden nach Quelle” — supports array rows or legacy object map. */
export const customerSourceBreakdownRows = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  return Object.entries(value).map(([key, count]) => ({
    source: key === "website" ? "webseite" : key,
    registrationSite: undefined,
    count,
  }));
};

export const translateServiceLabel = (t, booking) => {
  const stored = booking?.serviceLabel ?? booking;
  const storedString = typeof stored === "string" ? stored : "";
  const optionString =
    typeof booking?.hoffmannBookingOption === "string"
      ? booking.hoffmannBookingOption
      : "";
  const key =
    booking?.serviceKey ||
    SERVICE_LABEL_TO_KEY[storedString] ||
    LEGACY_SERVICE_LABEL_TO_KEY[storedString] ||
    LEGACY_SERVICE_LABEL_TO_KEY[optionString];
  if (key) {
    const msgKey = `service.${key}`;
    const translated = t(msgKey);
    if (translated !== msgKey) return translated;
    if (SERVICE_KEY_TO_LABEL[key]) return SERVICE_KEY_TO_LABEL[key];
  }
  return storedString ? normalizeLegacyServiceLabel(storedString) : "";
};

/** Customer Anmerkungen (booking form); includes address hints when present. */
export const formatBookingCustomerNotes = (booking) => {
  if (!booking) return "";
  const parts = [];
  const explicit = String(booking.customerNotes ?? "").trim();
  if (explicit) {
    parts.push(explicit);
  } else if (booking.customerNotes === undefined) {
    const legacy = String(booking.notes ?? "").trim();
    if (legacy) parts.push(legacy);
  }
  const addrNotes = String(booking.serviceAddress?.notes ?? "").trim();
  if (addrNotes && !parts.includes(addrNotes)) parts.push(addrNotes);
  return parts.join("\n\n");
};

/** Admin-only notes for the editable field (legacy bookings keep notes as customer text). */
export const internalNotesValue = (booking) => {
  if (!booking) return "";
  if (booking.customerNotes !== undefined && booking.customerNotes !== null) {
    return booking.notes || "";
  }
  return "";
};

export const periodLabel = (t, period) => {
  if (!period?.from && !period?.to) return t("dateRange.inPeriod");
  if (period.from && period.to) {
    return t("dateRange.range", { from: period.from, to: period.to });
  }
  if (period.from) return t("dateRange.fromDate", { date: period.from });
  return t("dateRange.toDate", { date: period.to });
};
